import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Leaf, Heart, Zap, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const benefits = [
    {
      icon: <Leaf className="w-8 h-8 md:w-12 md:h-12 text-primary" />,
      title: "Made with Super Seeds",
      description: "Packed with powerful seeds rich in nutrients"
    },
    {
      icon: <Heart className="w-8 h-8 md:w-12 md:h-12 text-primary" />,
      title: "Naturally Sweetened",
      description: "Only natural dates, no refined sugar"
    },
    {
      icon: <Zap className="w-8 h-8 md:w-12 md:h-12 text-primary" />,
      title: "Energy Boosting",
      description: "Perfect fuel for your active lifestyle"
    },
    {
      icon: <Shield className="w-8 h-8 md:w-12 md:h-12 text-primary" />,
      title: "Rich in Fiber",
      description: "Healthy fats and essential minerals"
    }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      rating: 5,
      comment: "These energy bites are perfect for my morning workout! Love the natural sweetness."
    },
    {
      name: "Rajesh Kumar",
      rating: 5,
      comment: "Finally a healthy snack that actually tastes good! No guilt, all energy."
    },
    {
      name: "Ananya Reddy",
      rating: 5,
      comment: "I keep these in my desk drawer. Perfect afternoon snack."
    }
  ];

  return (
    <div className="noise-overlay">
      {/* Hero Section */}
      <section className="py-8 md:py-16 lg:py-24 bg-gradient-to-b from-background to-muted" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Text */}
            <div className="space-y-5 md:space-y-8 text-center lg:text-left order-2 lg:order-1">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-tight" data-testid="hero-heading">
                Smart Energy Snacks Made From Super Seeds
              </h1>
              <p className="text-base md:text-lg lg:text-xl leading-relaxed text-muted-foreground" data-testid="hero-subtext">
                Healthy energy bites crafted from pumpkin seeds, sunflower seeds, sesame seeds, flax seeds, natural dates, and dark chocolate.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/products" data-testid="hero-cta-order" className="w-full sm:w-auto">
                  <button className="btn-accent flex items-center justify-center space-x-2 w-full sm:w-auto">
                    <span>Order Now</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/about" data-testid="hero-cta-learn" className="w-full sm:w-auto">
                  <button className="btn-outline w-full sm:w-auto">Learn More</button>
                </Link>
              </div>
            </div>

            {/* Right: Product Image */}
            <div className="relative order-1 lg:order-2">
              <div className="relative z-10">
                <img
                  src="https://customer-assets.emergentagent.com/job_seedoza-shop-1/artifacts/dt117c6h_WhatsApp%20Image%202026-03-13%20at%205.37.53%20PM.jpeg"
                  alt="Seedoza Energy Bites - Smart Energy from Nature"
                  className="w-full h-auto rounded-2xl md:rounded-3xl shadow-hover"
                  data-testid="hero-product-image"
                />
                {/* Shop Now Button Overlay - Hidden on mobile */}
                <Link to="/products" className="hidden md:block absolute bottom-6 left-6 lg:bottom-8 lg:left-8" data-testid="hero-image-shop-button">
                  <button className="btn-accent px-6 md:px-10 py-3 md:py-4 text-base md:text-lg font-bold shadow-2xl">
                    SHOP NOW
                  </button>
                </Link>
              </div>
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 md:w-96 h-64 md:h-96 bg-primary/10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase Section */}
      <section className="section-spacing" data-testid="product-showcase-section">
        <div className="container-custom">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-3 md:mb-4">Our Products</h2>
            <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover our range of nutritious energy snacks
            </p>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {products.map((product) => (
                <Link to={`/products/${product.id}`} key={product.id}>
                  <div className="product-card group" data-testid={`product-card-${product.id}`}>
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-6 space-y-2 md:space-y-4">
                      <h3 className="text-lg md:text-2xl font-heading font-semibold">{product.name}</h3>
                      <p className="text-sm md:text-base text-muted-foreground line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-xl md:text-2xl font-bold text-primary">
                          ₹{product.variants[0]?.price}
                        </span>
                        <span className="btn-primary text-xs md:text-sm py-2 px-4 md:py-2 md:px-6" data-testid={`view-product-${product.id}`}>
                          View Details
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-sm md:text-lg text-muted-foreground">Loading products...</p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-spacing bg-muted/30" data-testid="benefits-section">
        <div className="container-custom">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-3 md:mb-4">Why Choose Seedoza?</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="feature-card text-center" data-testid={`benefit-card-${index}`}>
                <div className="flex justify-center mb-3 md:mb-4">{benefit.icon}</div>
                <h3 className="text-sm md:text-xl font-heading font-semibold mb-1 md:mb-2">{benefit.title}</h3>
                <p className="text-xs md:text-base text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="section-spacing bg-secondary text-secondary-foreground" data-testid="brand-story-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center space-y-4 md:space-y-6">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-3 md:mb-4">Our Mission</h2>
            <p className="text-sm md:text-lg lg:text-xl leading-relaxed">
              At Seedoza, we believe snacking should be both delicious and nourishing. Our mission is to create natural snacks made from powerful ingredients that support healthier lifestyles.
            </p>
            <p className="text-sm md:text-lg lg:text-xl leading-relaxed">
              We source the finest seeds and ingredients to craft energy snacks that fuel your day naturally.
            </p>
            <Link to="/about">
              <button className="btn-accent mt-4 md:mt-8" data-testid="read-our-story">
                Read Our Story
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-spacing" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-3 md:mb-4">What Our Customers Say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="feature-card" data-testid={`testimonial-card-${index}`}>
                <div className="flex items-center space-x-1 mb-3 md:mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-sm md:text-base text-foreground mb-3 md:mb-4 italic">&quot;{testimonial.comment}&quot;</p>
                <p className="font-heading font-semibold text-sm md:text-base">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 lg:py-32 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 md:mb-6">
            Ready to Fuel Your Day Naturally?
          </h2>
          <p className="text-sm md:text-xl mb-6 md:mb-8 max-w-2xl mx-auto">
            Experience the power of super seeds with Seedoza Energy Bites
          </p>
          <Link to="/products">
            <button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 md:px-12 py-4 md:py-5 text-base md:text-lg font-semibold shadow-lg transition-transform hover:-translate-y-1 active:scale-95" data-testid="cta-shop-now">
              Shop Now
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
